import { serializeJsonLd } from "../shared/utils/seo";
import React, { useMemo } from 'react';
import Head from 'next/head';
import { AimOutlined } from '@ant-design/icons';
import BaseHead from '../components/Base/BaseHead';
import BaseListItem from '../components/Base/BaseListItem';
import ButtonBox from '../components/Base/ButtonBox';
import CityFilter, {
  ALL_CITIES,
  extractCities,
  filterByCity,
} from '../components/Base/CityFilter';
import ListEmptyState from '../components/Base/ListEmptyState';
import { getCourts } from '../lib/firestoreAdmin';
import { courtMapper } from '../shared/mappers/base-list-mapper';

export async function getStaticProps() {
  const courts = (await getCourts()).sort((a, b) =>
    a.kort_ad.localeCompare(b.kort_ad, 'tr'),
  );

  return {
    props: { courts },
    revalidate: 300,
  };
}

export default function Kortlar({ courts }: { courts: Courts.Court[] }) {
  const list = useMemo(() => courtMapper(courts), [courts]);
  const cities = useMemo(() => extractCities(list), [list]);
  const [selectedCity, setSelectedCity] = useCityFilter();
  const filteredList = useMemo(
    () => filterByCity(list, selectedCity),
    [list, selectedCity],
  );

  const pageSchema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'CollectionPage',
        name: 'Türkiye Tenis Kortları',
        description:
          'Türkiye genelindeki tenis kortlarını şehre göre keşfet. Kort özellikleri, zemin türü ve konum bilgilerini incele.',
        url: 'https://tenis.io/kortlar',
        breadcrumb: {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Ana Sayfa', item: 'https://tenis.io' },
            {
              '@type': 'ListItem',
              position: 2,
              name: 'Tenis Kortları',
              item: 'https://tenis.io/kortlar',
            },
          ],
        },
      },
      {
        '@type': 'ItemList',
        name: 'Türkiye Tenis Kortları',
        numberOfItems: list.length,
        itemListElement: list.map((court, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          name: court.title,
          url: `https://tenis.io${court.url}`,
        })),
      },
    ],
  };

  return (
    <>
      <BaseHead
        pageTitle="Türkiye Tenis Kortları"
        description="İstanbul, Ankara, İzmir ve Türkiye genelindeki tenis kortlarını keşfet. Şehre göre filtrele; zemin, açık-kapalı kort ve konum bilgilerini incele."
      />
      <Head>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: serializeJsonLd(pageSchema) }}
        />
      </Head>

      <div className="container mt-xl mb-xl">
        <div className="page-header">
          <div className="page-header-left">
            <span className="page-header-icon" aria-hidden="true">
              <AimOutlined />
            </span>
            <div className="page-header-text">
              <div className="page-header-title-row">
                <h1 className="page-header-title">Türkiye Tenis Kortları</h1>
                <span className="page-header-count">{filteredList.length}</span>
              </div>
              <p className="page-header-subtitle">
                Yakınındaki kortu bul, özelliklerini incele ve oyuna başla
              </p>
            </div>
          </div>
        </div>

        <ButtonBox />

        <CityFilter
          cities={cities}
          value={selectedCity}
          onChange={setSelectedCity}
          totalCount={list.length}
          filteredCount={filteredList.length}
        />

        <div className="list" id="list">
          {!filteredList.length && (
            <ListEmptyState
              onReset={selectedCity !== ALL_CITIES ? () => setSelectedCity(ALL_CITIES) : undefined}
            />
          )}
          {filteredList.map(item => (
            <BaseListItem key={item.url} listItem={item} />
          ))}
        </div>
      </div>
    </>
  );
}
import { useCityFilter } from '../lib/useCityFilter';
